import { useCallback, useState } from "react";
import {
  getCreditDueDate,
  getPaymentTerm,
  isPurchaseOverdue,
} from "../utils/purchaseUtils";

const getDateKey = (date) => (date ? date.toISOString().split("T")[0] : "");

const usePurchaseFilters = ({
  defaultStartDate = "",
  defaultEndDate = "",
  includeSearch = false,
  includeClientType = false,
} = {}) => {
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [paymentTermFilter, setPaymentTermFilter] = useState("");
  const [dueStartDate, setDueStartDate] = useState("");
  const [dueEndDate, setDueEndDate] = useState("");
  const [overdueFilter, setOverdueFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [hasClientId, setHasClientId] = useState(null);

  const applySharedFilters = useCallback(
    (purchase) => {
      if (!purchase) return false;
      const createdDate = new Date(purchase.createdAt);
      const createdKey = Number.isNaN(createdDate.getTime())
        ? ""
        : createdDate.toISOString().split("T")[0];
      const withinDateRange =
        (!startDate || createdKey >= startDate) &&
        (!endDate || createdKey <= endDate);
      if (!withinDateRange) return false;

      const term = getPaymentTerm(purchase);
      if (paymentTermFilter && term !== paymentTermFilter) return false;

      const overdue = isPurchaseOverdue(purchase);
      if (overdueFilter === "overdue" && !overdue) return false;
      if (overdueFilter === "current" && overdue) return false;

      if (dueStartDate || dueEndDate) {
        if (term !== "credit") return false;
        const dueDate = getCreditDueDate(purchase);
        const dueKey = getDateKey(dueDate);
        if (!dueKey) return false;
        if (dueStartDate && dueKey < dueStartDate) return false;
        if (dueEndDate && dueKey > dueEndDate) return false;
      }

      return true;
    },
    [
      startDate,
      endDate,
      paymentTermFilter,
      dueStartDate,
      dueEndDate,
      overdueFilter,
    ]
  );

  const resetFilters = useCallback(
    ({ start = defaultStartDate, end = defaultEndDate } = {}) => {
      setStartDate(start);
      setEndDate(end);
      setPaymentTermFilter("");
      setDueStartDate("");
      setDueEndDate("");
      setOverdueFilter("");
      if (includeSearch) setSearchTerm("");
      if (includeClientType) setHasClientId(null);
    },
    [defaultStartDate, defaultEndDate, includeSearch, includeClientType]
  );

  return {
    filters: {
      startDate,
      endDate,
      paymentTermFilter,
      dueStartDate,
      dueEndDate,
      overdueFilter,
      searchTerm,
      hasClientId,
    },
    setters: {
      setStartDate,
      setEndDate,
      setPaymentTermFilter,
      setDueStartDate,
      setDueEndDate,
      setOverdueFilter,
      setSearchTerm,
      setHasClientId,
    },
    applySharedFilters,
    resetFilters,
  };
};

export default usePurchaseFilters;
